import { useFirebase } from '@/components/providers/site/FirebaseProvider'
import { useSocialController } from '@/core/hooks/useSWRApi'
import { ChatAccessLevel, UserDocument } from '@rallycry/api-suite-typescript'
import { EntityType } from '@rallycry/api-suite-typescript/dist/models/EntityType'
import { ChatControllerApi } from '@rallycry/social-api-typescript'
import {
  ChatDocument,
  MemberKind
} from '@rallycry/social-api-typescript/dist/models'
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where
} from 'firebase/firestore'
import { values } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import useSWRSubscription, { SWRSubscriptionOptions } from 'swr/subscription'
import { useOrganization } from '../organization/useOrganization'
import { EntityOptions, useReadEntity } from '../useEntity'
import { useUserAccount } from '../user/useUserAccount'
import { mapChatResult } from './useChatMessages'

export const useChatRoomDocument = (options: EntityOptions) => {
  const { isOrgModerator } = useOrganization()
  const { firestore } = useFirebase()
  const { user } = useUserAccount()
  const { ctrl } = useSocialController(ChatControllerApi)

  const docKey = `Social_Chat/${options.idOrKey}`
  const db = firestore

  const {
    read: { data: schema }
  } = useReadEntity({
    key: 'useSocialChatSchema',
    persist: true,
    ...options,
    idOrKey: 1,
    read: async () => {
      try {
        return await ctrl().getChatSchema()
      } catch (error) {
        console.log(error)
        return { schema: -1 }
      }
    }
  })

  const data = useSWRSubscription(
    [docKey, schema?.schema],
    ([docKey], { next }: SWRSubscriptionOptions<ChatDocument, Error>) => {
      const ref = doc(firestore, docKey)
      const unsub = onSnapshot(
        ref,
        res => {
          const data = res.data() as ChatDocument
          if (data?._schema !== schema?.schema) {
            // read forces an update of schema
            ctrl().readChat({ id: options.idOrKey?.toString()! })
          }
          next(null, data)
        },
        err => next(err)
      )
      return () => unsub()
    },
    { suspense: false }
  )

  const leaveChat = useCallback(
    async () => ctrl().leaveChat({ id: options.idOrKey?.toString()! }),
    [ctrl, options.idOrKey]
  )

  const addParticipant = useCallback(
    async (user?: UserDocument | null) =>
      ctrl().grantChatMember({
        id: options.idOrKey?.toString()!,
        chatGrantMemberCommand: {
          id: user?.id?.toString()!,
          externalId: user?.firebaseUid,
          kind: MemberKind.FIREBASE_USER,
          accessLevel: ChatAccessLevel.NORMAL,
          image: user?.image,
          name: user?.name!
        }
      }),
    [ctrl, options.idOrKey]
  )

  const removeParticipant = useCallback(
    async (fbUid: string, rcUid: string) =>
      ctrl().revokeChatMember({
        id: options.idOrKey?.toString()!,
        chatRevokeMemberCommand: {
          member: rcUid,
          access: fbUid
        }
      }),
    [ctrl, options.idOrKey]
  )

  const members = useMemo(() => {
    return values(data?.data?.members || {})
  }, [data?.data?.members])

  const collectionKey = `Social_Chat/${options.idOrKey}/messages`
  const getAllMessagesCsv = useCallback(async () => {
    const c = collection(db, collectionKey)
    const q = query(c, where('unsent', '==', false))
    const res = await getDocs(q)
    const messages = res.docs.map(it => mapChatResult(it))
    const csv = messages
      .map(it => {
        const authorName = members.find(
          member => member.id === it.author?.toString()
        )?.name
        const date = new Date(it.dateCreated).toISOString()
        return `${it.author},${authorName},${date},${it.text}`
      })
      .join('\n')
      // add header
      .replace(/^/, 'author,authorName,dateCreated,text\n')
    return csv
  }, [db, collectionKey, members])

  const typing = async () =>
    ctrl().typingChat({ id: options.idOrKey?.toString()! })

  const markAsRead = async () =>
    ctrl().markAsReadChat({ id: options.idOrKey?.toString()! })

  const room = data.data
  const entityType = (room?.attributes?.['entityType'] ||
    ('' as unknown)) as EntityType
  const entityId = Number(room?.attributes?.['entityId'] || ('' as unknown))
  const childType = (room?.attributes?.['childType'] ||
    ('' as unknown)) as EntityType
  const childId = Number(room?.attributes?.['childId'] || ('' as unknown))

  return {
    ...data,
    room: data.data,
    members,
    isModerator:
      isOrgModerator ||
      !!room?.members?.some(
        member =>
          member.id === user?.id?.toString() &&
          member.accessLevel === ChatAccessLevel.MODERATOR
      ),
    addParticipant,
    removeParticipant,
    typing,
    markAsRead,
    leaveChat,
    getAllMessagesCsv,
    attributes: { entityType, entityId, childType, childId }
  }
}
